import { useEffect, useState, useCallback } from "react";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { DateInput } from "@/components/DateInput";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Building2, Plus, Trash2, Search, Eye, EyeOff, Save, Image as ImageIcon, CheckCircle2, Circle } from "lucide-react";
import { ClientLogo } from "@/components/ClientLogo";
import { ClientProfileIO } from "@/components/ClientProfileIO";

const ONB_STATUS = [
  { k: "prospect", l: "Prospek", cls: "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300" },
  { k: "documents", l: "Kumpul Dokumen", cls: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300" },
  { k: "setup", l: "Setup", cls: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300" },
  { k: "active", l: "Aktif", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300" },
];
const OBLIGATIONS = ["PPh 21", "PPh 23", "PPh Final", "PPh 25", "PPh Badan", "PPN"];

const blankProfile = (cid) => ({
  client_id: cid, logo: "", email: "", onboarding_status: "active", pics: [],
  tax_info: { pkp: false, efin: "", coretax_user: "", obligations: [] },
  legal: { nib: "", akta_no: "", notaris: "", sk_kemenkumham: "", domicile: "", established: "" },
  credentials: [], onboarding_checklist: [],
});

export default function ClientWorkspace() {
  const { t } = useI18n();
  const { user } = useAuth();
  const canEdit = ["super_admin", "supervisor"].includes(user?.role);
  const [clients, setClients] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [sel, setSel] = useState(null);
  const [f, setF] = useState(blankProfile(""));
  const [q, setQ] = useState("");
  const [showPass, setShowPass] = useState({});

  const loadAll = useCallback(async () => {
    const [c, p] = await Promise.all([
      api.get("/clients"),
      api.get("/client-profiles"),
    ]);
    setClients(c.data); setProfiles(p.data);
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const statusOf = (cid) => profiles.find((p) => p.client_id === cid)?.onboarding_status || "active";
  const openClient = async (c) => {
    setSel(c);
    const { data } = await api.get(`/client-profiles/${c.id}`);
    setF({ ...blankProfile(c.id), ...data, tax_info: { pkp: false, efin: "", coretax_user: "", obligations: [], ...(data.tax_info || {}) }, legal: { nib: "", akta_no: "", notaris: "", sk_kemenkumham: "", domicile: "", established: "", ...(data.legal || {}) } });
    setShowPass({});
  };
  const save = async () => {
    try {
      await api.put(`/client-profiles/${sel.id}`, f);
      toast.success("Profil klien tersimpan"); loadAll();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const onLogo = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) { toast.error("Logo terlalu besar (maks ~1MB)"); return; }
    const r = new FileReader(); r.onload = () => setF((s) => ({ ...s, logo: r.result })); r.readAsDataURL(file);
  };

  // array helpers
  const upd = (key, i, k, v) => setF((s) => ({ ...s, [key]: s[key].map((x, idx) => idx === i ? { ...x, [k]: v } : x) }));
  const addRow = (key, obj) => setF((s) => ({ ...s, [key]: [...s[key], obj] }));
  const rmRow = (key, i) => setF((s) => ({ ...s, [key]: s[key].filter((_, idx) => idx !== i) }));
  const setTax = (k, v) => setF((s) => ({ ...s, tax_info: { ...s.tax_info, [k]: v } }));
  const setLegal = (k, v) => setF((s) => ({ ...s, legal: { ...s.legal, [k]: v } }));
  const toggleObl = (o) => setTax("obligations", (f.tax_info.obligations || []).includes(o) ? f.tax_info.obligations.filter((x) => x !== o) : [...(f.tax_info.obligations || []), o]);

  const filtered = clients.filter((c) => !q || `${c.name} ${c.code}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-4" data-testid="client-workspace-page">
      <div className="page-sticky flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-head text-2xl font-bold tracking-tight flex items-center gap-2"><Building2 className="h-6 w-6 text-primary" /> {t("nav.clients")}</h1>
          <p className="text-sm text-muted-foreground mt-1">Profil klien lengkap, kredensial akses, dan status onboarding — internal.</p>
        </div>
        {canEdit && <ClientProfileIO onDone={() => { loadAll(); if (sel) openClient(sel); }} />}
      </div>

      <div className="grid lg:grid-cols-[320px_1fr] gap-4">
        <Card className="p-3 space-y-2 h-fit">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Cari klien..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-9" data-testid="cw-search" />
          </div>
          <div className="space-y-1.5 max-h-[70vh] overflow-auto">
            {filtered.map((c) => {
              const st = ONB_STATUS.find((s) => s.k === statusOf(c.id)) || ONB_STATUS[3];
              const prof = profiles.find((p) => p.client_id === c.id);
              return (
                <button key={c.id} onClick={() => openClient(c)} data-testid={`cw-client-${c.code}`}
                  className={`w-full text-left rounded-lg border p-2.5 flex items-center gap-3 transition-colors ${sel?.id === c.id ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/50"}`}>
                  <ClientLogo id={c.id} v={c.logo_v} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{c.name}</div>
                    <div className="text-[11px] text-muted-foreground">{c.code}{!c.is_active && " · nonaktif"}</div>
                  </div>
                  <Badge className={`text-[9px] ${st.cls}`}>{st.l}</Badge>
                </button>
              );
            })}
          </div>
        </Card>

        {!sel ? (
          <Card className="p-10 flex items-center justify-center text-muted-foreground text-sm">Pilih klien untuk melihat profil & onboarding.</Card>
        ) : (
          <Card className="p-4 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                {f.logo ? <div className="h-12 w-12 rounded-md bg-secondary flex items-center justify-center overflow-hidden"><img src={f.logo} alt="" className="max-h-full max-w-full object-contain" /></div>
                  : <ClientLogo id={sel.id} v={sel.logo_v} className="h-12 w-12" icon="h-5 w-5" />}
                <div><div className="font-head font-semibold">{sel.name}</div><div className="text-xs text-muted-foreground">{sel.code} · NPWP {sel.npwp || "—"}</div></div>
              </div>
              {canEdit
                ? <Button onClick={save} data-testid="cw-save"><Save className="h-4 w-4 mr-2" />Simpan Profil</Button>
                : <Badge variant="outline" className="text-xs" data-testid="cw-readonly-badge">Hanya-baca (Super Admin / Supervisor)</Badge>}
            </div>

            <fieldset disabled={!canEdit} className="border-0 p-0 m-0 min-w-0 disabled:opacity-100">
            <Tabs defaultValue="profil">
              <TabsList className="flex-wrap h-auto">
                <TabsTrigger value="profil" data-testid="cw-tab-profil">Profil</TabsTrigger>
                <TabsTrigger value="pajak" data-testid="cw-tab-pajak">Perpajakan</TabsTrigger>
                <TabsTrigger value="legal" data-testid="cw-tab-legal">Legalitas</TabsTrigger>
                <TabsTrigger value="kredensial" data-testid="cw-tab-cred">Kredensial</TabsTrigger>
                <TabsTrigger value="onboarding" data-testid="cw-tab-onb">Onboarding</TabsTrigger>
              </TabsList>

              <TabsContent value="profil" className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Logo Perusahaan</Label>
                    <div className="flex items-center gap-3">
                      <div className="h-20 w-32 rounded-md border border-dashed flex items-center justify-center bg-secondary/30 overflow-hidden">
                        {f.logo ? <img src={f.logo} alt="" className="max-h-full max-w-full object-contain" data-testid="cw-logo-preview" /> : <ClientLogo id={sel.id} v={sel.logo_v} className="h-full w-full" icon="h-6 w-6" />}
                      </div>
                      <div className="space-y-2">
                        {!f.logo && <p className="text-[11px] text-muted-foreground max-w-[220px]" data-testid="cw-logo-fallback-note">Belum diisi — otomatis memakai logo dari Pengaturan Invoice klien ini.</p>}
                        <Input type="file" accept="image/*" onChange={onLogo} data-testid="cw-logo-file" className="max-w-[200px]" />
                        {f.logo && <Button variant="outline" size="sm" onClick={() => setF((s) => ({ ...s, logo: "" }))}><Trash2 className="h-4 w-4 mr-1" />Hapus</Button>}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="space-y-1.5"><Label>Email Perusahaan</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="cw-email" /></div>
                    <div className="space-y-1.5"><Label>Status Onboarding</Label>
                      <Select value={f.onboarding_status} onValueChange={(v) => setF({ ...f, onboarding_status: v })}>
                        <SelectTrigger data-testid="cw-onb-status"><SelectValue /></SelectTrigger>
                        <SelectContent>{ONB_STATUS.map((s) => <SelectItem key={s.k} value={s.k}>{s.l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between"><Label>Kontak PIC</Label><Button variant="outline" size="sm" onClick={() => addRow("pics", { name: "", position: "", phone: "", email: "", whatsapp: "" })} data-testid="cw-add-pic"><Plus className="h-4 w-4 mr-1" />PIC</Button></div>
                  {f.pics.map((p, i) => (
                    <div key={i} className="grid md:grid-cols-5 gap-2 items-center rounded-md border border-border p-2" data-testid={`cw-pic-${i}`}>
                      <Input placeholder="Nama" value={p.name} onChange={(e) => upd("pics", i, "name", e.target.value)} />
                      <Input placeholder="Jabatan" value={p.position} onChange={(e) => upd("pics", i, "position", e.target.value)} />
                      <Input placeholder="HP" value={p.phone} onChange={(e) => upd("pics", i, "phone", e.target.value)} />
                      <Input placeholder="Email" value={p.email} onChange={(e) => upd("pics", i, "email", e.target.value)} />
                      <div className="flex gap-1"><Input placeholder="WhatsApp" value={p.whatsapp} onChange={(e) => upd("pics", i, "whatsapp", e.target.value)} /><Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => rmRow("pics", i)}><Trash2 className="h-4 w-4" /></Button></div>
                    </div>
                  ))}
                  {f.pics.length === 0 && <p className="text-xs text-muted-foreground">Belum ada PIC.</p>}
                </div>
              </TabsContent>

              <TabsContent value="pajak" className="space-y-3">
                <div className="flex items-center justify-between rounded-md border border-border p-3">
                  <Label className="cursor-pointer">Pengusaha Kena Pajak (PKP)</Label>
                  <Switch checked={!!f.tax_info.pkp} onCheckedChange={(v) => setTax("pkp", v)} data-testid="cw-pkp" />
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>EFIN</Label><Input value={f.tax_info.efin} onChange={(e) => setTax("efin", e.target.value)} data-testid="cw-efin" /></div>
                  <div className="space-y-1.5"><Label>Akun Coretax / DJP (username)</Label><Input value={f.tax_info.coretax_user} onChange={(e) => setTax("coretax_user", e.target.value)} data-testid="cw-coretax" /></div>
                </div>
                <div className="space-y-2">
                  <Label>Kewajiban Pajak</Label>
                  <div className="flex flex-wrap gap-2">
                    {OBLIGATIONS.map((o) => {
                      const on = (f.tax_info.obligations || []).includes(o);
                      return <button key={o} onClick={() => toggleObl(o)} data-testid={`cw-obl-${o}`} className={`text-xs rounded-full px-3 py-1 border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>{o}</button>;
                    })}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="legal" className="grid md:grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>NIB / OSS</Label><Input value={f.legal.nib} onChange={(e) => setLegal("nib", e.target.value)} data-testid="cw-nib" /></div>
                <div className="space-y-1.5"><Label>No. Akta Pendirian</Label><Input value={f.legal.akta_no} onChange={(e) => setLegal("akta_no", e.target.value)} data-testid="cw-akta" /></div>
                <div className="space-y-1.5"><Label>Notaris</Label><Input value={f.legal.notaris} onChange={(e) => setLegal("notaris", e.target.value)} /></div>
                <div className="space-y-1.5"><Label>SK Kemenkumham</Label><Input value={f.legal.sk_kemenkumham} onChange={(e) => setLegal("sk_kemenkumham", e.target.value)} data-testid="cw-sk" /></div>
                <div className="space-y-1.5"><Label>Tanggal Berdiri</Label><DateInput value={f.legal.established} onChange={(e) => setLegal("established", e.target.value)} /></div>
                <div className="space-y-1.5 md:col-span-2"><Label>Alamat Domisili</Label><Textarea rows={2} value={f.legal.domicile} onChange={(e) => setLegal("domicile", e.target.value)} data-testid="cw-domicile" /></div>
              </TabsContent>

              <TabsContent value="kredensial" className="space-y-2">
                <div className="flex items-center justify-between"><Label>Brankas Kredensial / Akses</Label><Button variant="outline" size="sm" onClick={() => addRow("credentials", { label: "", url: "", username: "", password: "", note: "" })} data-testid="cw-add-cred"><Plus className="h-4 w-4 mr-1" />Kredensial</Button></div>
                {f.credentials.map((c, i) => (
                  <div key={i} className="grid md:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 items-center rounded-md border border-border p-2" data-testid={`cw-cred-${i}`}>
                    <Input placeholder="Label (mis. DJP Online)" value={c.label} onChange={(e) => upd("credentials", i, "label", e.target.value)} />
                    <Input placeholder="URL" value={c.url} onChange={(e) => upd("credentials", i, "url", e.target.value)} />
                    <Input placeholder="Username" value={c.username} onChange={(e) => upd("credentials", i, "username", e.target.value)} />
                    <div className="relative">
                      <Input type={showPass[i] ? "text" : "password"} placeholder="Password" value={c.password} onChange={(e) => upd("credentials", i, "password", e.target.value)} className="pr-9" data-testid={`cw-cred-pass-${i}`} />
                      <button type="button" onClick={() => setShowPass((s) => ({ ...s, [i]: !s[i] }))} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" data-testid={`cw-cred-toggle-${i}`}>{showPass[i] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                    </div>
                    <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => rmRow("credentials", i)} data-testid={`cw-cred-del-${i}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
                {f.credentials.length === 0 && <p className="text-xs text-muted-foreground">Belum ada kredensial tersimpan.</p>}
              </TabsContent>

              <TabsContent value="onboarding" className="space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                  {ONB_STATUS.map((s, idx) => (
                    <div key={s.k} className="flex items-center gap-2">
                      <button onClick={() => setF({ ...f, onboarding_status: s.k })} className={`text-xs rounded-full px-3 py-1 border ${f.onboarding_status === s.k ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>{s.l}</button>
                      {idx < ONB_STATUS.length - 1 && <span className="text-muted-foreground">→</span>}
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between"><Label>Checklist Dokumen Onboarding</Label><Button variant="outline" size="sm" onClick={() => addRow("onboarding_checklist", { label: "", done: false })} data-testid="cw-add-check"><Plus className="h-4 w-4 mr-1" />Item</Button></div>
                {f.onboarding_checklist.map((it, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-md border border-border p-2" data-testid={`cw-check-${i}`}>
                    <button onClick={() => upd("onboarding_checklist", i, "done", !it.done)} data-testid={`cw-check-toggle-${i}`}>{it.done ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <Circle className="h-5 w-5 text-muted-foreground" />}</button>
                    <Input placeholder="Dokumen / langkah" value={it.label} onChange={(e) => upd("onboarding_checklist", i, "label", e.target.value)} className={it.done ? "line-through text-muted-foreground" : ""} />
                    <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => rmRow("onboarding_checklist", i)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
                {f.onboarding_checklist.length === 0 && <p className="text-xs text-muted-foreground">Belum ada checklist.</p>}
              </TabsContent>
            </Tabs>
            </fieldset>
          </Card>
        )}
      </div>
    </div>
  );
}
